import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PostAddIcon from '@mui/icons-material/PostAdd';
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import AddTastingDialog from "../components/AddTastingDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 20;

const columns = [
  {
    field: "productName",
    headerName: "Product",
    width: 200,
    sortable: false,
  },
  {
    field: "varietal",
    headerName: "Varietal",
    width: 200,
    sortable: false,
  },
  {
    field: "producer",
    headerName: "Producer",
    width: 200,
    sortable: false,
  },
  {
    field: "vintage",
    headerName: "Vintage",
    width: 100,
    sortable: false,
  },
  {
    field: "sku",
    headerName: "SKU",
    width: 150,
    sortable: false,
  },
  {
    field: "author",
    headerName: "Author",
    width: 200,
    sortable: false,
  },
  {
    field: "date",
    headerName: "Date",
    width: 150,
    sortable: false,
    valueGetter: (value) =>
      value
        ? format(new Date(value), "yyyy-MM-dd")
        : "",
  },
];

export default function WineTasting() {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);
  const [filter, setFilter] = useState({
    productName: "",
    varietal: "",
    producer: "",
    vintage: "",
    sku: "",
    author: "",
  });
  const [tastings, setTastings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [rowId, setRowId] = useState(null);

  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  let active = useRef(false);

  const showDialog = () => {
    setRowId(null);
    setDialogVisible(true)
  };
  const hideDialog = (reload) => {
    setDialogVisible(false);
    setRowId(null);
    if (reload) {
      loadTastings(true);
    }
  };
  const onRowClick = (params) => {
    setRowId(params.row.id);
    setDialogVisible(true);
  };

  const loadTastings = async (reset) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const params = new URLSearchParams({
      where: JSON.stringify({
        productName: filter.productName ? { contains: filter.productName } : undefined,
        varietal: filter.varietal ? { contains: filter.varietal } : undefined,
        producer: filter.producer ? { contains: filter.producer } : undefined,
        vintage: filter.vintage ? parseInt(filter.vintage) : undefined,
        sku: filter.sku ? { contains: filter.sku } : undefined,
        author: filter.author ? { contains: filter.author } : undefined
      }),
      skip: reset ? 0 : paginationModel.page * PAGE_SIZE,
      take: PAGE_SIZE,
    });
    const response = await fetchWithInterceptor(
      `/api/tastings/?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      }
    );
    if (response.ok) {
      if (!active.current) {
        return;
      }

      const data = await response.json();
      if (reset) {
        setPaginationModel({ ...paginationModel, page: 0 });
      }
      setTastings(data.tastings);
      setRowCount(data.count);
    } else {
      console.log(response);
      appContext.showMessage(
        "There was an error getting the tastings",
        "error"
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    loadTastings(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadTastings(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleCreate = async (data) => {
    setIsLoading(true);
    const response = await fetchWithInterceptor("/api/tastings", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        'x-access-token': authContext.getToken()
      },
    });
    if (response.ok) {
      hideDialog(true);
    } else {
      appContext.showMessage(
        "There was an error creating the tasting",
        "error"
      );
    }
    setIsLoading(false);
  };

  const handleUpdate = async (data) => {
    setIsLoading(true);
    const response = await fetchWithInterceptor(`/api/tastings/${rowId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        'x-access-token': authContext.getToken()
      },
    });
    if (response.ok) {
      hideDialog(true);
    } else {
      appContext.showMessage(
        "There was an error updating the tasting",
        "error"
      );
    }
    setIsLoading(false);
  };

  const onSave = async (data) => {
    if (rowId) {
      handleUpdate(data);
    } else {
      handleCreate(data);
    }
  };

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Manage ZYN's tasting events and catalog the wines featured in each session. Use this data to enrich our product database and maintain a detailed history of all tastings.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="Product"
                  variant="outlined"
                  value={filter.productName}
                  onChange={(e) =>
                    setFilter({ ...filter, productName: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="Varietal"
                  variant="outlined"
                  value={filter.varietal}
                  onChange={(e) =>
                    setFilter({ ...filter, varietal: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="Producer"
                  variant="outlined"
                  value={filter.producer}
                  onChange={(e) =>
                    setFilter({ ...filter, producer: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="Vintage"
                  variant="outlined"
                  value={filter.vintage}
                  onChange={(e) =>
                    setFilter({ ...filter, vintage: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="SKU"
                  variant="outlined"
                  value={filter.sku}
                  onChange={(e) =>
                    setFilter({ ...filter, sku: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="Author"
                  variant="outlined"
                  value={filter.author}
                  onChange={(e) =>
                    setFilter({ ...filter, author: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
          <Divider />

          <Grid container justifyContent="flex-end" style={{ padding: 16 }}>
            <Grid>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<PostAddIcon />}
                onClick={showDialog}
              >
                Add Tasting
              </Button>
            </Grid>
          </Grid>
          <DataGrid
            rows={tastings}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSize={PAGE_SIZE}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            loading={isLoading}
            onRowClick={onRowClick}
            rowCount={rowCount}
            paginationMode="server"
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
              "& .MuiChip-root.pending": {
                color: "rgb(102, 60, 0)",
                backgroundColor: "rgb(255, 244, 229)",
              },
              "& .MuiChip-root.withdrawn": {
                color: "rgb(30, 70, 32)",
                backgroundColor: "rgb(237, 247, 237)",
              },
            }}
          />
        </Paper>
        <AddTastingDialog open={dialogVisible} onClose={hideDialog} onSave={onSave} rowId={rowId} />
      </Container>
    </>
  );
}